import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { TabsList } from '../../../Components/Tab/Tab'
import { IStoreState, useAppDispatch, useAppSelector } from '../../../redux';
import { useOutletContext } from '../../../providers';
import { Box, Button, Tab, Tabs } from '@mui/material';
import { a11yProps } from '../../../Components/Tab/utils';
import { fetchSingleOutletLayoutsAsync, ITablesLayout } from '../../../redux/tables';
import { UpsertReservationDialog } from '../../Reservations/dialogs/UpsertReservationDialog';
import { defaultReservation, IReservation } from '../../../redux/reservations';

interface ILayoutTabsPanelProps {
    selectedDate: string;
    activeTab: string;
    onTabChange: (tab: string) => void;
    onInsertSuccess: () => void;
}

export const LayoutTabsPanel: React.FC<ILayoutTabsPanelProps> = ({ selectedDate, activeTab, onTabChange, onInsertSuccess }) => {
    const { outlet_details: { outlet_uuid } } = useOutletContext();
    const [openReservationDialog, setOpenReservationDialog] = useState<IReservation | null>(null);
    const dispatch = useAppDispatch();
    const customizer = useAppSelector((state: IStoreState) => state.customizer);

    const multipleDataArray = useAppSelector((storeState: IStoreState) => storeState.tables.layouts_list_without_tables.data);

    useEffect(() => {
        if (!outlet_uuid || multipleDataArray.length > 0) return;
        dispatch(fetchSingleOutletLayoutsAsync({ outlet_uuid, exclude_layout_without_tables: true }));
    }, [outlet_uuid, dispatch]); // Removed multipleDataArray to prevent unnecessary re-fetching

    const tabs = useMemo(() => multipleDataArray, [multipleDataArray]);

    useEffect(() => {
        if (tabs.length > 0 && !activeTab) {
            onTabChange(tabs[0].layout_uuid as string);
        }
    }, [tabs, activeTab, onTabChange]);

    const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: string) => {
        onTabChange(newValue);
    }, [onTabChange]);

    return (
        <>
            <Box sx={{ borderBottom: 1, borderColor: "divider", display: "flex", justifyContent: 'space-between', alignItems: "flex-end" }}>
                <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange}
                    sx={{
                        '& .MuiTab-root': {
                            color: customizer.activeMode === 'dark' ? '#fff' : 'rgba(0, 0, 0, 0.87)',
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: customizer.activeMode === 'dark' ? '#fff' : 'rgba(0, 0, 0, 0.87)',
                        }
                    }}
                >
                    {tabs.map((tab, index) => (
                        <Tab key={tab.layout_uuid} label={tab.layout_name} value={tab.layout_uuid} {...a11yProps(index)} />
                    ))}
                </Tabs>
                <Button sx={{ my: 1 }} variant="contained" onClick={() => {
                    const layout = tabs.find((layout) => layout.layout_uuid === activeTab);
                    if (!layout) return;

                    setOpenReservationDialog({
                        ...defaultReservation,
                        layout_uuid: layout.layout_uuid as string,
                        layout_name: layout.layout_name,
                        reservation_date: selectedDate,
                        outlet_uuid: outlet_uuid || "",
                    });
                }}>New Reservation</Button>
            </Box>

            {openReservationDialog && (
                <UpsertReservationDialog 
                    open={true}
                    reservation={openReservationDialog}
                    onClose={() => setOpenReservationDialog(null)}
                    onSuccess={onInsertSuccess} // ✅ This function is already being optimized in the parent
                />
            )}
        </>
    );
};
